import React, { useState, useEffect } from 'react';
import { useQuery } from '../lib/queryClient';
import superjson from 'superjson';
import { Link, useSearchParams } from 'react-router-dom';
import { resolveFeedPostCoverImage } from '../helpers/feedPostImages';
import styles from './news.module.css';

const ADMIN_EMAIL = 'ryand32@gmail.com';

// ── API helpers ────────────────────────────────────────────────────
async function fetchPosts(limit = 20, offset = 0) {
  const res = await fetch(`/_api/feed/posts?limit=${limit}&offset=${offset}`);
  const text = await res.text();
  return superjson.parse<{ posts: any[]; total: number }>(text);
}

async function fetchPost(slug: string) {
  const res = await fetch(`/_api/feed/posts?slug=${encodeURIComponent(slug)}`);
  const text = await res.text();
  return superjson.parse<{ post: any }>(text);
}

// ── Utilities ─────────────────────────────────────────────────────
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function estimateReadTime(content: string): string {
  const words = content.trim().split(/\s+/).length;
  const mins = Math.max(1, Math.ceil(words / 200));
  return `${mins} min read`;
}

function getFirstChar(title: string): string {
  return title.charAt(0).toUpperCase();
}

function parseTags(tags: string | null): string[] {
  if (!tags) return [];
  try {
    return JSON.parse(tags);
  } catch {
    return [];
  }
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
}

function readImgAttr(tag: string, attr: string): string | null {
  const regex = new RegExp(`${attr}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');
  const match = tag.match(regex);
  return (match?.[2] ?? match?.[3] ?? match?.[4] ?? '').trim() || null;
}

function normalizeLazyImageTags(html: string): string {
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    const lazySrc =
      readImgAttr(tag, 'data-src') ||
      readImgAttr(tag, 'data-lazy-src') ||
      readImgAttr(tag, 'data-original');

    let nextTag = tag;

    if (lazySrc) {
      const safeSrc = lazySrc.replace(/"/g, '&quot;');
      if (/\bsrc\s*=/.test(nextTag)) {
        nextTag = nextTag.replace(
          /\bsrc\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i,
          `src="${safeSrc}"`,
        );
      } else {
        nextTag = nextTag.replace(/^<img\b/i, `<img src="${safeSrc}"`);
      }
    }

    if (!/\bloading\s*=/.test(nextTag)) {
      nextTag = nextTag.replace(/^<img\b/i, '<img loading="lazy"');
    }
    if (!/\bdecoding\s*=/.test(nextTag)) {
      nextTag = nextTag.replace(/^<img\b/i, '<img decoding="async"');
    }

    return nextTag;
  });
}

// ── Lightweight Markdown renderer (no external dependency) ─────────
function simpleMarkdown(md: string): string {
  const normalized = normalizeLazyImageTags(md);

  return (
    normalized
      // Code blocks (must come before inline code)
      .replace(
        /```[\w]*\n?([\s\S]*?)```/g,
        (_, code) =>
          `<pre><code>${code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`,
      )
      // HR
      .replace(/^---$/gm, '<hr />')
      // Headings
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Blockquote
      .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
      // Unordered list items
      .replace(/^\* (.+)$/gm, '<li>$1</li>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // Ordered list items
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // Bold + italic
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Links
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      )
      // Images
      .replace(
        /!\[([^\]]*)\]\(([^)]+)\)/g,
        '<img src="$2" alt="$1" loading="lazy" decoding="async" style="max-width:100%;border-radius:4px;" />',
      )
      // Wrap bare text lines into paragraphs (lines not already wrapped)
      .replace(/^(?!<[a-z])(.*\S.*)$/gm, '<p>$1</p>')
      // Clean up adjacent blockquotes/lists
      .replace(/<\/blockquote>\n<blockquote>/g, '\n')
      .replace(/<\/li>\n<li>/g, '</li><li>')
  );
}

function MarkdownContent({ content }: { content: string }) {
  const html = React.useMemo(() => simpleMarkdown(content), [content]);
  return <div className={styles.articleBody} dangerouslySetInnerHTML={{ __html: html }} />;
}

// ── Single Post View ───────────────────────────────────────────────
function SinglePostView({ slug }: { slug: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['feed-post', slug],
    queryFn: () => fetchPost(slug),
  });

  if (isLoading) return <div className={styles.loading}>Loading…</div>;
  if (isError || !data?.post) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyGlyph}>¶</div>
        <p className={styles.emptyTitle}>Post not found</p>
        <p className={styles.emptyText}>This post may have been removed or never existed.</p>
      </div>
    );
  }

  const { post } = data;
  const tags = parseTags(post.tags);
  const coverImage = resolveFeedPostCoverImage(post);

  return (
    <div className={styles.articlePage}>
      <div className={styles.articleHero}>
        <div className={styles.articleHeroInner}>
          <Link to="/news" className={styles.backLink}>
            ← Feed
          </Link>
          {tags.length > 0 && (
            <div className={styles.articleTags}>
              {tags.map((t: string) => (
                <span key={t} className={styles.articleTagDark}>
                  {t}
                </span>
              ))}
            </div>
          )}
          <h1 className={styles.articleTitle}>{post.title}</h1>
          <div className={styles.articleByline}>
            <div className={styles.authorAvatar} style={{ background: '#C9822A' }}>
              T
            </div>
            <div>
              <div className={styles.articleAuthorName}>{post.author_name || 'TaskingTech'}</div>
              <div className={styles.articleDate}>
                {formatDate(post.published_at || post.created_at)} ·{' '}
                {estimateReadTime(post.content)}
              </div>
            </div>
          </div>
        </div>
        {coverImage && (
          <div className={styles.articleCover} style={{ maxWidth: 760, margin: '0 auto' }}>
            <img
              src={coverImage}
              alt={post.title}
              className={styles.articleCoverImg}
              loading="lazy"
              decoding="async"
            />
          </div>
        )}
      </div>
      <MarkdownContent content={post.content} />
    </div>
  );
}

// ── Post Card ─────────────────────────────────────────────────────
function PostCard({ post, index }: { post: any; index: number }) {
  const tags = parseTags(post.tags);
  const coverImage = resolveFeedPostCoverImage(post);
  const delayClass = [
    styles.animDelay1,
    styles.animDelay2,
    styles.animDelay3,
    styles.animDelay4,
    styles.animDelay5,
    styles.animDelay6,
  ][index % 6];

  return (
    <Link
      to={`/news?slug=${post.slug}`}
      className={`${styles.postCard} ${styles.animFadeUp} ${delayClass}`}
    >
      <div className={styles.postCardCover}>
        {coverImage ? (
          <img
            src={coverImage}
            alt={post.title}
            className={styles.postCardCoverImg}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className={styles.postCardCoverPlaceholder}>
            <span className={styles.postCardGlyph}>{getFirstChar(post.title)}</span>
          </div>
        )}
      </div>
      {tags.length > 0 && (
        <div className={styles.postCardTags}>
          {tags.slice(0, 2).map((t: string) => (
            <span key={t} className={styles.tag}>
              {t}
            </span>
          ))}
        </div>
      )}
      <h3 className={styles.postCardTitle}>{post.title}</h3>
      {post.excerpt && <p className={styles.postCardExcerpt}>{post.excerpt}</p>}
      <div className={styles.postCardFooter}>
        <span className={styles.postCardDate}>{formatDate(post.published_at)}</span>
        <span className={styles.postCardReadMore}>Read →</span>
      </div>
    </Link>
  );
}

// ── Main Feed Page ─────────────────────────────────────────────────
export default function NewsPage() {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('slug');

  const { data, isLoading } = useQuery({
    queryKey: ['feed-posts'],
    queryFn: () => fetchPosts(20, 0),
    enabled: !slug,
  });

  // Check if current user is admin (via session endpoint)
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    fetch('/_api/session')
      .then((r) => r.json())
      .then((d: any) => {
        if (d?.user?.email === ADMIN_EMAIL) setIsAdmin(true);
      })
      .catch(() => {});
  }, []);

  if (slug) {
    return (
      <>
        <Masthead isAdmin={isAdmin} />
        <SinglePostView slug={slug} />
      </>
    );
  }

  const posts = data?.posts || [];
  const featured = posts[0];
  const featuredCoverImage = featured ? resolveFeedPostCoverImage(featured) : null;
  const rest = posts.slice(1);

  return (
    <>
      <div className={styles.page}>
        {isAdmin && (
          <div className={styles.adminBar}>
            <div className={styles.adminBarInner}>
              <span className={styles.adminBadge}>Admin</span>
              <span className={styles.adminHint}>
                Use TaskingBot to publish, edit, or delete posts on this feed.
              </span>
            </div>
          </div>
        )}
        <Masthead isAdmin={isAdmin} />

        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loading}>Loading…</div>
          ) : posts.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyGlyph}>¶</div>
              <p className={styles.emptyTitle}>Nothing published yet</p>
              <p className={styles.emptyText}>
                {isAdmin ? 'Use TaskingBot to publish your first post.' : 'Check back soon.'}
              </p>
            </div>
          ) : (
            <>
              {featured && (
                <section className={`${styles.featuredSection} ${styles.animFadeUp}`}>
                  <div className={styles.featuredLabel}>Latest</div>
                  <Link to={`/news?slug=${featured.slug}`} className={styles.featuredPost}>
                    <div className={styles.featuredCover}>
                      {featuredCoverImage ? (
                        <img
                          src={featuredCoverImage}
                          alt={featured.title}
                          className={styles.featuredCoverImg}
                          loading="eager"
                          decoding="async"
                        />
                      ) : (
                        <div className={styles.featuredCoverPlaceholder}>
                          <span className={styles.featuredCoverGlyph}>
                            {getFirstChar(featured.title)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className={styles.featuredMeta}>
                      {parseTags(featured.tags).length > 0 && (
                        <div className={styles.featuredTags}>
                          {parseTags(featured.tags).map((t: string) => (
                            <span key={t} className={styles.tag}>
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                      <h2 className={styles.featuredTitle}>{featured.title}</h2>
                      {featured.excerpt && (
                        <p className={styles.featuredExcerpt}>{featured.excerpt}</p>
                      )}
                      <div className={styles.featuredFooter}>
                        <div className={styles.authorBlock}>
                          <div className={styles.authorAvatar}>T</div>
                          <div>
                            <div className={styles.authorName}>
                              {featured.author_name || 'TaskingTech'}
                            </div>
                            <div className={styles.postDate}>
                              {formatDate(featured.published_at)}
                            </div>
                          </div>
                        </div>
                        <span className={styles.readMore}>Read article →</span>
                      </div>
                    </div>
                  </Link>
                </section>
              )}

              {rest.length > 0 && (
                <section className={styles.gridSection}>
                  <div className={styles.gridHeader}>
                    <span className={styles.gridLabel}>
                      More from the feed — {rest.length} {rest.length === 1 ? 'post' : 'posts'}
                    </span>
                  </div>
                  <div className={styles.postsGrid}>
                    {rest.map((post: any, i: number) => (
                      <PostCard key={post.id} post={post} index={i} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Masthead({ isAdmin }: { isAdmin: boolean }) {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <header className={styles.masthead}>
      <div className={styles.mastheadInner}>
        <span className={styles.mastheadDate}>{dateStr}</span>
        <nav>
          <ul className={styles.mastheadLinks}>
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li>
              <Link to="/pricing">Pricing</Link>
            </li>
            {isAdmin && (
              <li>
                <Link to="/taskingbot">TaskingBot</Link>
              </li>
            )}
          </ul>
        </nav>
      </div>
      <div className={styles.mastheadTitle}>
        <h1 className={styles.siteWordmark}>
          tasking<span className={styles.mastheadAccent}>.</span>tech
        </h1>
        <p className={styles.siteTagline}>News &amp; Updates</p>
      </div>
    </header>
  );
}
